import React from "react";
import PropTypes from "prop-types";
import {
  FormattedMessage,
  defineMessages,
  useIntl,
} from "react-intl";
import st from "core/shared-translations";
import classes from "./signin-security.scss";
import BoxedContainer from "components/boxed-container-view";
import { EditOutlinedIcon, PhoneNumberOutlinedIcon } from "@getgo/chameleon-icons/react";
import { AlertV2, Button, Dialog, Typography } from '@getgo/chameleon-web-react-wrapper';
import ResponsiveDialog from 'components/dialog-view.js';
import { CheckmarkStaticPictogram } from '@getgo/chameleon-pictograms/react';
import OTPInput from "containers/otp-input-container";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import EmailVerificationContainer from "containers/email-verification-container";

const t = defineMessages({
  "recovery-number.title": {
    id: "recovery-number.title",
    defaultMessage: "Recovery Number",
    description: "Title string for the recovery number section",
  },
  "recovery-number.protection-message": {
    id: "recovery-number.protection-message",
    defaultMessage: "Set a recovery phone number to help protect your account.",
    description: "Message explaining the purpose of setting a recovery phone number",
  },
  "recovery-number.set-button": {
    id: "recovery-number.set-button",
    defaultMessage: "Set a phone number",
    description: "Button text to set a phone number",
  },
  "recovery-number.send.code": {
    id: "recovery-number.send.code",
    defaultMessage: "Send Code",
    description: "Button text to send verification code",
  },
  "recovery-number.cancel": {
    id: "recovery-number.cancel",
    defaultMessage: "Cancel",
    description: "Button text to cancel setting phone number",
  },
  "recovery-number.delete": {
    id: "recovery-number.delete",
    defaultMessage: "Delete",
    description: "Button text to delete the recovery phone number",
  },
   "recovery-number.delete-number": {
    id: "recovery-number.delete-number",
    defaultMessage: "Delete number",
    description: "Button text to delete the recovery phone number",
  },
  "recovery-number.add-title": {
    id: "recovery-number.add-title",
    defaultMessage: "Add recovery phone number",
    description: "Title for the dialog to add a recovery phone number",
  },
  "recovery-number.number-input.title": {
    id: "recovery-number.number-input.title",
    defaultMessage: "Phone number",
    description: "Label for the input field to enter phone number",
  },
  "recovery-number.phone-input.title": {
    id: "recovery-number.phone-input.title",
    defaultMessage: "Mobile number",
    description: "Title string for the recovery phone number input",
  },
  "recovery-number.phone-input.helper-text": {
    id: "recovery-number.phone-input.helper-text",
    defaultMessage: "A 6-digit verification code will be sent to this number",
    description: "Helper text for the phone number input field",
  },
  "recovery-number.verify-phone-number.title": {
    id: "recovery-number.verify-phone-number.title",
    defaultMessage: "Verify recovery phone number",
    description: "Title for the dialog to verify a recovery phone number",
  },
  "recovery-number.delete-phone-number.title": {
    id: "recovery-number.delete-phone-number.title",
    defaultMessage: "Delete recovery phone number?",
    description: "Title for the dialog to delete a recovery phone number",
  },
  "recovery-number.delete-phone-number.message": {
    id: "recovery-number.delete-phone-number.message",
    defaultMessage: "You will no longer be asked to verify your identity when making changes to your GoTo profile.",
    description: "Message prompting user to confirm deletion of recovery phone number",
  },
  "recovery-number.deleted-number.title": {
    id: "recovery-number.deleted-number.title",
    defaultMessage: "Recovery phone number deleted",
    description: "Title for the dialog confirming deletion of recovery phone number",
  },
  "recovery-number.deleted-number.message": {
    id: "recovery-number.deleted-number.message",
    defaultMessage: "If you want help to protect your account in the future, please add a new recovery phone number.",
    description: "Message prompting user to confirm deletion of recovery phone number",
  },
  "recovery-number.verify.code": {
    id: "recovery-number.verify.code",
    defaultMessage: "Verify",
    description: "Button text to verify the recovery phone number",
  },
  "recovery-number.verify-phone-number.message": {
    id: "recovery-number.verify-phone-number.message",
    defaultMessage: "Enter the 6-digit verification code sent to {br}<b>{phoneNumber}</b>",
    description: "Message prompting user to enter verification code for phone number",
  },
  "recovery-number.phone-number-verified.title": {
    id: "recovery-number.phone-number-verified.title",
    defaultMessage: "Recovery phone number verified",
    description: "Title for the dialog confirming phone number verification",
  },
  "recovery-number.got-it.cta": {
    id: "recovery-number.got-it.cta",
    defaultMessage: "Got it",
    description: "Button text to acknowledge the phone number verification",
  },
  "recovery-number.phone-number-verified.message": {
    id: "recovery-number.phone-number-verified.message",
    defaultMessage: "You're all set!",
    description: "Message confirming successful verification of the recovery phone number",
  },
  "recovery-number.did-not-receive-text": {
    id: "recovery-number.did-not-receive-text",
    defaultMessage: "Didn't receive a text?",
    description: "Text prompting user to resend verification code if not received",
  },
  "recovery-number.resend-code": {
    id: "recovery-number.resend-code",
    defaultMessage: "Resend Code",
    description: "Button text to resend the verification code",
  },
  "recovery-number.resend-code.timer": {
    id: "recovery-number.resend-code.timer",
    defaultMessage: "Resend available in {timer}s",
    description: "Message indicating the timer for resending the verification code",
  },
  'recovery-number.edit-title': {
    id: 'recovery-number.edit-title',
    defaultMessage: 'Edit recovery phone number',
    description: 'Title for the dialog to edit a recovery phone number',
  },
  'recovery-number.invalid-code': {
    id: 'recovery-number.invalid-code',
    defaultMessage: 'Incorrect code. Try again.',
    description: 'Error message displayed when the user enters an invalid verification code',
  },
  'recovery-number.invalid-phone-number': {
    id: 'recovery-number.invalid-phone-number',
    defaultMessage: 'Please enter a valid mobile number',
    description: 'Error message displayed when the user enters an invalid phone number',
  },

});

const RecoveryNumberView = ({
  isLoading,
  getMobileNumberIsLoading,
  isPhoneNumberSetupAlready,
  onClickSetPhoneNumber,
  editPhoneNumberClick,
  closeModal,
  maskedNumber,
  phoneNumber,
  onChangePhoneNumber,
  modalState,
  onSave,
  timer,
  onClickResendCode,
  onBlur,
  onComplete,
  disableVerifyCode,
  onClickEditPhoneNumber,
  disableSendCode,
  closeEmailVerificationModal,
  completeProcess,
  sendVerificationEmail,
  isOpenVerificationModal,
  error,
}) => {
  const intl = useIntl();
  return (
    <div>
      <BoxedContainer
        isLoading={getMobileNumberIsLoading}
        title={<FormattedMessage {...t["recovery-number.title"]} />}
        showCTAButton={isPhoneNumberSetupAlready}
        buttonIcon={<EditOutlinedIcon />}
        buttonText={<FormattedMessage {...st["shared.edit"]} />}
        onClick={onClickEditPhoneNumber}
        boxIcon={<PhoneNumberOutlinedIcon color="#000000" />}
      >
        {isPhoneNumberSetupAlready ? maskedNumber :
        <div className={classes["recovery-number-view"]}>
          <AlertV2 variant="info">
            <FormattedMessage {...t["recovery-number.protection-message"]} />
          </AlertV2>
          <Button
            className={classes["setPhoneNumber"]}
            variant="secondary"
            onClick={onClickSetPhoneNumber}>
            <FormattedMessage {...t["recovery-number.set-button"]} />
          </Button>
        </div>}
      </BoxedContainer>
      <ResponsiveDialog
        closable
        onClosed={closeModal}
        open={modalState === "AddRecoveryPhoneNumber" || modalState === "EmailVerification"}
        customClassName={classes["recoveryNumberDialogChildren"]}
        title={editPhoneNumberClick ?
          isOpenVerificationModal? intl.formatMessage(st["shared.email-verification"]) :
          <FormattedMessage {...t["recovery-number.edit-title"]} />
          :<FormattedMessage {...t["recovery-number.add-title"]} />}
        actions={isOpenVerificationModal ? null :
        <div className={classes["editNumberButton"]}>
          {editPhoneNumberClick ? (
            <div>
              <Button
                id="delete-phone-number"
                onClick={(e)=>onSave(e,"delete-phone-number")}
                variant="tertiary">
                <Typography color="type-color-danger">
                  <FormattedMessage {...t["recovery-number.delete-number"]} />
                </Typography>
              </Button>
            </div>
          ) : <div />}
          <div>
            <Button
              id="cancel"
              onClick={(e)=>onSave(e,"cancel")}
              variant="tertiary"
            >
              <FormattedMessage {...t["recovery-number.cancel"]} />
            </Button>
            <Button
              id="send-verification-code"
              onClick={(e)=>onSave(e,"send-verification-code")}
              disabled={disableSendCode || isLoading}
            >
              <FormattedMessage {...t["recovery-number.send.code"]} />
            </Button>
          </div>
        </div>}
      >
        {isOpenVerificationModal ?
          <EmailVerificationContainer
            completeProcess={completeProcess}
            closeModal={closeEmailVerificationModal}
            sendVerificationEmail={sendVerificationEmail}
          /> :
          <div className={classes["emailVerification"]}>
            <div style={{padding: "9px"}}>
              <Typography variant="caption-medium-strong">
                <FormattedMessage {...t["recovery-number.phone-input.title"]} />
              </Typography>
              <PhoneInput
                id="phoneInput"
                country="us" // Default country
                value={phoneNumber}
                onChange={onChangePhoneNumber}
                buttonStyle={error === "invalid-phone-number"? { borderColor: 'red',backgroundColor: 'white' } : { backgroundColor: 'white' }}
                placeholder={intl.formatMessage(t["recovery-number.phone-input.title"])}
                defaultErrorMessage={intl.formatMessage(t["recovery-number.phone-input.helper-text"])}
                inputStyle={error === "invalid-phone-number" ?
                  { borderColor: 'red', borderWidth: '1px',width: '100%' } :
                  { width: '100%', borderColor: '#ced4da' }}
              />
              {error === "invalid-phone-number" ?
                <Typography variant="caption-medium" color="type-color-danger">
                  <FormattedMessage {...t["recovery-number.invalid-phone-number"]} />
                </Typography> :
                <Typography variant="caption-medium" color="type-color-secondary">
                  <FormattedMessage {...t["recovery-number.phone-input.helper-text"]} />
                </Typography>}
            </div>
          </div>}
      </ResponsiveDialog>
      <ResponsiveDialog
        closable
        onClosed={closeModal}
        open={modalState === "DeleteRecoveryPhoneNumber"}
        customClassName={classes["recoveryNumberDialogChildren"]}
        title={<FormattedMessage {...t["recovery-number.delete-phone-number.title"]} />}
        actions={<React.Fragment>
          <Button
            id="confirm-delete"
            variant="danger"
            onClick={(e)=>onSave(e,"delete-confirm")}
          >
            <FormattedMessage {...t["recovery-number.delete"]} />
          </Button>
          <Button
            id="delete-back"
            onClick={(e)=>onSave(e,"delete-back")}
            variant="tertiary">
            <FormattedMessage {...st['shared.back']} />
          </Button>
        </React.Fragment>}
      >
        <div style={{paddingLeft: "9px"}}>
          <Typography>
            <FormattedMessage
              {...t["recovery-number.delete-phone-number.message"]} />
          </Typography>
        </div>
      </ResponsiveDialog>
      <ResponsiveDialog
        closable
        onClosed={closeModal}
        open={modalState === "PhoneNumberDeleted"}
        customClassName={classes["recoveryNumberDialogChildren"]}
        title={<FormattedMessage {...t["recovery-number.deleted-number.title"]} />}
        actions={<React.Fragment>
          <Button
            id="got-it"
            onClick={(e)=>onSave(e,"got-it")}
          >
            <FormattedMessage {...t["recovery-number.got-it.cta"]} />
          </Button>
        </React.Fragment>}
      >
        <div style={{paddingLeft: "9px"}}>
          <Typography>
            <FormattedMessage
              {...t["recovery-number.deleted-number.message"]} />
          </Typography>
        </div>
      </ResponsiveDialog>
      <ResponsiveDialog
        closable
        onClosed={closeModal}
        open={modalState === "VerifyRecoveryPhoneNumber"}
        customClassName={classes["recoveryNumberDialogChildren"]}
        title={<FormattedMessage {...t["recovery-number.verify-phone-number.title"]} />}
        actions={<React.Fragment>
          <Button
            id="verify-code"
            onClick={(e)=>onSave(e,"verify-code")}
            disabled={disableVerifyCode}
          >
            <FormattedMessage {...t["recovery-number.verify.code"]} />
          </Button>
          <Button
            id="back"
            onClick={(e)=>onSave(e,"back")}
            variant="tertiary">
            <FormattedMessage {...st["shared.back"]} />
          </Button>
        </React.Fragment>}
      >
        <div className={classes["verifyContainer"]}>
          {error === "invalid-code" &&
            <AlertV2 variant="danger">
              <FormattedMessage {...t["recovery-number.invalid-code"]} />
            </AlertV2>}
          <Typography>
            <FormattedMessage
              {...t["recovery-number.verify-phone-number.message"]}
              values={{
                br: <br />,
                b: (chunks) => <b>{chunks}</b>,
                phoneNumber: phoneNumber }} />
          </Typography>
          <OTPInput
            onBlur={onBlur}
            onComplete={onComplete}
            error={error}
            resetKey={modalState === "VerifyRecoveryPhoneNumber" ? modalState : null}
          />
          <div className={classes["resendCodeBlock"]}>
            <Typography variant="caption-medium" color={`${timer > 0 ? "type-color-disabled" : "type-color-default"}`}>
              <FormattedMessage {...t["recovery-number.did-not-receive-text"]} />
            </Typography>
            <Button
              disabled={timer > 0}
              variant="tertiary"
              data-testid="resend-code-button"
              onClick={onClickResendCode}
              className={classes["resendCodeCTA"]}
            >
              <FormattedMessage {...t["recovery-number.resend-code"]} />
            </Button>
          </div>
          {timer > 0 &&
            <Typography variant="caption-medium-strong">
              <FormattedMessage {...t["recovery-number.resend-code.timer"]} values={{ timer }} />
            </Typography>}
        </div>
      </ResponsiveDialog>
      <ResponsiveDialog
        closable
        onClosed={closeModal}
        open={modalState === "PhoneNumberVerified"}
        customClassName={classes["recoveryNumberDialogChildren"]}
        title={<FormattedMessage {...t["recovery-number.phone-number-verified.title"]} />}
        actions={<React.Fragment>
          <Button
            id="got-it"
            onClick={(e)=>onSave(e,"got-it")}
          >
            <FormattedMessage {...t["recovery-number.got-it.cta"]} />
          </Button>
        </React.Fragment>}
      >
        <div className={classes["verifiedContainer"]}>
          <CheckmarkStaticPictogram size="72px" />
          <Typography style={{ textAlign: 'center' }}>
            <FormattedMessage
              {...t["recovery-number.phone-number-verified.message"]} />
          </Typography>
        </div>
      </ResponsiveDialog>
    </div>
  );
};

RecoveryNumberView.propTypes = {
  isLoading: PropTypes.bool,
  getMobileNumberIsLoading: PropTypes.bool.isRequired,
  isPhoneNumberSetupAlready: PropTypes.bool,
  onClickSetPhoneNumber: PropTypes.func.isRequired,
  editPhoneNumberClick: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  maskedNumber: PropTypes.string.isRequired,
  phoneNumber: PropTypes.string,
  onChangePhoneNumber: PropTypes.func.isRequired,
  modalState: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired,
  timer: PropTypes.number,
  onClickResendCode: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  disableVerifyCode: PropTypes.bool.isRequired,
  onClickEditPhoneNumber: PropTypes.func.isRequired,
  disableSendCode: PropTypes.bool.isRequired,
  completeProcess: PropTypes.func.isRequired,
  sendVerificationEmail: PropTypes.func.isRequired,
  closeEmailVerificationModal: PropTypes.func.isRequired,
  isOpenVerificationModal: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
};

export default RecoveryNumberView;
